import React, { useState, useEffect, useCallback } from 'react';
import PageIndexProps from './props';
import style from './style.module.scss';
import classNames from '../../../utils/classNames';

function PageIndex(props: PageIndexProps) {
  const {
    items,
    activeItemId,
    onItemClick,
    title,
    scrollOffset = 80,
    scrollBehavior = 'smooth'
  } = props;

  const [activeItem, setActiveItem] = useState<string>(activeItemId || '');

  // Handle smooth scrolling to section
  const handleItemClick = useCallback((itemId: string) => {
    const item = items.find(i => i.id === itemId);
    if (!item) return;
    
    // Try to get element from ref first, then fallback to selector
    let element: HTMLElement | null = null;
    
    if (item.ref?.current) {
      element = item.ref.current;
    } else if (item.selector) {
      element = document.querySelector(item.selector);
    } else {
      // Fallback to ID-based selector
      element = document.getElementById(itemId);
    }
    
    if (element) {
      const elementTop = element.offsetTop - scrollOffset;
      
      window.scrollTo({
        top: elementTop,
        behavior: scrollBehavior
      });
      
      onItemClick?.(itemId);
    }
  }, [items, scrollOffset, scrollBehavior, onItemClick]);

  // Track active section based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + scrollOffset + 50;
      
      let newActiveItem = '';

      for (const item of items) {
        let element: HTMLElement | null = null;

        if (item.ref?.current) {
          element = item.ref.current;
        } else if (item.selector) {
          element = document.querySelector(item.selector);
        } else {
          element = document.getElementById(item.id);
        }

        if (element) {
          const elementTop = element.offsetTop;
          if (scrollPosition >= elementTop) {
            newActiveItem = item.id;
          }
        }
      }

      setActiveItem(newActiveItem);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [items, scrollOffset]);

  // Update active item when activeItemId prop changes
  useEffect(() => {
    if (activeItemId !== undefined) {
      setActiveItem(activeItemId);
    }
  }, [activeItemId]);

  return (
    <div className={classNames('pageIndex', style, props.classNames)}>
      <div className={classNames('pageIndexHeader', style)}>
        <h2 className={classNames('pageIndexTitle', style)}>{title}</h2>
      </div>
      <nav className={classNames('pageIndexNav', style)}>
        <ul className={classNames('pageIndexList', style)}>
          {items.map((item) => (
            <li key={item.id} className={classNames('pageIndexItem', style)}>
              <button
                type="button"
                className={classNames(
                  'pageIndexButton', 
                  style,
                  { 'active': activeItem === item.id }
                )}
                onClick={() => handleItemClick(item.id)}
                aria-current={activeItem === item.id ? 'page' : undefined}
              >
                {item.title}
              </button>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}

export default PageIndex; 